import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { Link } from 'react-router-dom'
import { ScanLine } from 'lucide-react'
import toast from 'react-hot-toast'
import { useAuth } from '@/contexts/AuthContext'
import { getApiErrorMessage as errorText } from '@/lib/api-client'
import { productionApi, hubContainerCode, type ProductionCommand, type ProductionJob, type ProductionLot, type ProductionSession, type ProductionProgress, type ProductionGenealogy } from '@/lib/production-api'
import { Genealogy, ProductionOutputs } from './ProductionOutputs'
import { ProductionScanner } from './ProductionScanner'
import { useProductionCapabilities } from '@/lib/use-production-capabilities'
import { catalogExample, productionCatalogSchema, type ProductionCatalog } from '@/lib/production-catalog'

interface Selection {
  workstationId: string
  jobId: string
  sessionId: string
}

const emptySelection: Selection = { workstationId: '', jobId: '', sessionId: '' }

function readSelection(key: string): Selection {
  try {
    const value: unknown = JSON.parse(localStorage.getItem(key) || 'null')
    if (value && typeof value === 'object' &&
      'workstationId' in value && typeof value.workstationId === 'string' &&
      'jobId' in value && typeof value.jobId === 'string' &&
      'sessionId' in value && typeof value.sessionId === 'string') {
      return { workstationId: value.workstationId, jobId: value.jobId, sessionId: value.sessionId }
    }
  } catch {
    // Storage is optional; the production API remains the source of truth.
  }
  return emptySelection
}

function saveSelection(key: string, selection: Selection) {
  try {
    localStorage.setItem(key, JSON.stringify(selection))
  } catch {
    // Browser policies may disable persistent storage.
  }
}

function positiveDecimal(value: string) {
  return /^\d+(?:\.\d+)?$/.test(value) && /[1-9]/.test(value)
}

function QueryState({ loading, error, errorMessage, empty, emptyContent, retry, children }: Readonly<{
  loading: boolean
  error: boolean
  errorMessage?: string
  empty: boolean
  emptyContent?: ReactNode
  retry: () => void
  children: ReactNode
}>) {
  if (loading) return <output className="block">A carregar...</output>
  if (error) return (
    <div role="alert" className="space-y-2 text-red-700">
      <p>{errorMessage || 'Não foi possível carregar os dados.'}</p>
      <button className="btn btn-secondary" onClick={retry}>Tentar novamente</button>
    </div>
  )
  if (empty) return <>{emptyContent ?? <p className="text-gray-500">Sem registos disponíveis.</p>}</>
  return <>{children}</>
}

export default function ProductionPage() {
  const { user } = useAuth()
  const capabilities = useProductionCapabilities()
  if (!user) return null
  if (capabilities.isLoading) return <output className="block">A verificar disponibilidade da produção...</output>
  if (capabilities.isError) return (
    <div role="alert">
      <p>Não foi possível verificar a disponibilidade da produção.</p>
      <button className="btn btn-secondary mt-2" onClick={() => void capabilities.refetch()}>Tentar novamente</button>
    </div>
  )
  if (!capabilities.data?.enabled || capabilities.data.mode === 'off') {
    return <p>A produção não está disponível neste tenant.</p>
  }
  return <ProductionWorkspace key={`${user.tenant_id}:${user.id}`} tenantId={user.tenant_id}
    userId={user.id} mode={capabilities.data.mode} canManage={capabilities.data.can_manage} />
}

function ProductionWorkspace({ tenantId, userId, mode, canManage }: Readonly<{
  tenantId: number
  userId: number
  mode: 'shadow' | 'active'
  canManage: boolean
}>) {
  const storageKey = `production:selection:${tenantId}:${userId}`
  const [selection, setSelection] = useState(() => readSelection(storageKey))
  const [uncertain, setUncertain] = useState<ProductionCommand | null>(null)
  const [notification, setNotification] = useState('')
  const [uncertainCatalog, setUncertainCatalog] = useState<ProductionCatalog | null>(null)
  const [view, setView] = useState<'operation' | 'history'>('operation')
  const [networkOnline, setNetworkOnline] = useState(navigator.onLine)
  const deviceId = useRef(`web:${crypto.randomUUID()}`)
  const editorRef = useRef<HTMLDivElement>(null)
  const outputsRef = useRef<HTMLDivElement>(null)
  const inFlight = useRef(false)
  const queryClient = useQueryClient()
  const scope = ['production', tenantId, userId] as const
  const connection = useQuery({
    queryKey: [...scope, 'connection'],
    queryFn: productionApi.capabilities,
    enabled: networkOnline,
    retry: false,
    refetchInterval: 20000,
    refetchOnWindowFocus: 'always',
  })
  const serverAvailable = networkOnline && connection.isSuccess && !connection.isError && connection.data.enabled
  const { workstationId, jobId, sessionId } = selection
  const workstations = useQuery({ queryKey: [...scope, 'workstations'], queryFn: productionApi.workstations })
  const validWorkstation = workstations.data?.some(w => w.id === workstationId) === true
  const jobs = useQuery({
    queryKey: [...scope, 'jobs', workstationId],
    queryFn: () => productionApi.jobs(workstationId),
    enabled: validWorkstation,
  })
  const sessions = useQuery({
    queryKey: [...scope, 'sessions', workstationId],
    queryFn: () => productionApi.sessions(workstationId),
    enabled: validWorkstation,
  })
  const detail = useQuery({
    queryKey: [...scope, 'session', sessionId],
    queryFn: () => productionApi.session(sessionId),
    enabled: validWorkstation && !!sessionId,
    refetchOnWindowFocus: false,
  })
  const currentJobId = detail.data?.job_id || jobId
  const progress = useQuery({
    queryKey: [...scope, 'progress', currentJobId, sessionId],
    queryFn: () => productionApi.progress(currentJobId, sessionId || undefined),
    enabled: validWorkstation && !!currentJobId,
    refetchInterval: 15000,
  })
  const lots = useQuery({
    queryKey: [...scope, 'lots', currentJobId],
    queryFn: () => productionApi.lots(currentJobId),
    enabled: validWorkstation && !!currentJobId,
    retry: false,
    refetchOnWindowFocus: false,
  })

  useEffect(() => { saveSelection(storageKey, selection) }, [selection, storageKey])
  useEffect(() => {
    const updateNetwork = () => setNetworkOnline(navigator.onLine)
    window.addEventListener('online', updateNetwork)
    window.addEventListener('offline', updateNetwork)
    return () => {
      window.removeEventListener('online', updateNetwork)
      window.removeEventListener('offline', updateNetwork)
    }
  }, [])

  const catalogMutation = useMutation({
    mutationFn: productionApi.importCatalog,
    retry: false,
    onSuccess: async () => {
      setUncertainCatalog(null)
      setNotification('Catálogo importado. Selecione novamente o posto e o trabalho.')
      setSelection(emptySelection)
      await queryClient.invalidateQueries({ queryKey: scope })
      toast.success('Catálogo importado.')
    },
    onError: (error: unknown, catalog) => {
      const status = isAxiosError(error) ? error.response?.status : undefined
      if (status === undefined || status >= 500 || status === 408) {
        setUncertainCatalog(catalog)
        setNotification('Resultado da importação incerto. Repita o mesmo catálogo, sem gerar novos identificadores.')
      } else {
        setUncertainCatalog(null)
        setNotification(errorText(error))
      }
    },
    onSettled: () => { inFlight.current = false },
  })

  const mutation = useMutation({
    mutationFn: productionApi.execute,
    retry: false,
    onSuccess: (session, command) => {
      setUncertain(null)
      setNotification(command.kind === 'confirm'
        ? 'Produção confirmada — etiquetas disponíveis nos contentores produzidos.'
        : command.kind === 'create'
          ? 'Rascunho criado — adicione os consumos e confirme a produção para gerar etiquetas.'
          : 'Consumo guardado no servidor — a produção continua em rascunho, sem etiquetas. Reveja e confirme a produção para concluir.')
      queryClient.setQueryData([...scope, 'session', session.id], session)
      setSelection(previous => ({ ...previous, sessionId: session.id, jobId: session.job_id }))
      void queryClient.invalidateQueries({ queryKey: [...scope, 'sessions', workstationId] })
      void queryClient.invalidateQueries({ queryKey: [...scope, 'lots'] })
      void queryClient.invalidateQueries({ queryKey: [...scope, 'progress'] })
      toast.success(command.kind === 'confirm' ? 'Produção confirmada.' : 'Registo guardado.')
    },
    onError: async (error: unknown, command) => {
      const status = isAxiosError(error) ? error.response?.status : undefined
      if (status === 409 || status === 412) {
        setUncertain(null)
        const message = 'Conflito: o registo foi recarregado. Reveja os dados antes de repetir. Reutilizar um contentor exige autorização explícita.'
        setNotification(`${message} ${errorText(error)}`)
        toast.error(message)
        const target = command.kind === 'create' ? command.body.id : command.sessionId
        await queryClient.invalidateQueries({ queryKey: [...scope, 'session', target] })
        await queryClient.invalidateQueries({ queryKey: [...scope, 'sessions', workstationId] })
        await queryClient.invalidateQueries({ queryKey: [...scope, 'lots'] })
        await queryClient.invalidateQueries({ queryKey: [...scope, 'progress'] })
      } else if (status === undefined || status >= 500 || status === 408) {
        // Keep the entire request, including its UUID and revision, for a safe retry.
        setUncertain(command)
        setNotification('Resultado incerto. Não crie outro registo: repita exatamente o mesmo pedido abaixo.')
      } else {
        setUncertain(null)
        setNotification(errorText(error))
      }
    },
    onSettled: () => { inFlight.current = false },
  })

  function execute(command: ProductionCommand) {
    if (!serverAvailable) {
      setNotification('Sem confirmação de ligação ao servidor. Verifique a ligação antes de submeter.')
      return
    }
    if (inFlight.current) return
    inFlight.current = true
    setNotification('')
    mutation.mutate(command)
  }

  const busy = !serverAvailable || mutation.isPending || !!uncertain || catalogMutation.isPending || !!uncertainCatalog
  const selectedJob = jobs.data?.find(job => job.id === currentJobId)
  const session = detail.data

  return (
    <div className="mx-auto max-w-6xl space-y-5">
      <div>
        <Link className="text-sm text-primary-700 md:hidden" to="/dashboard">← Dashboard</Link>
        <h1 className="text-2xl font-bold">Produção</h1>
        <p className="text-sm text-gray-600">Registos online de consumo e produção · Modo {mode}</p>
        <div role="status" className={`mt-2 flex flex-wrap items-center justify-between gap-2 rounded border p-3 ${serverAvailable ? 'border-green-300 bg-green-50' : 'border-amber-300 bg-amber-50'}`}>
          <span>{!networkOnline ? 'Sem ligação de rede' : connection.isPending ? 'A verificar ligação...' : serverAvailable ? 'Online — servidor acessível' : 'Rede ligada — servidor indisponível'}</span>
          <button className="btn btn-secondary" disabled={!networkOnline || connection.isFetching}
            onClick={() => void connection.refetch()}>Verificar ligação</button>
        </div>
        {mode === 'shadow' && <p className="mt-2 rounded bg-amber-50 p-3 text-amber-900">Modo shadow: registo de evidência sem execução operacional no ERP.</p>}
      </div>

      <nav aria-label="Área de produção" className="flex gap-2">
        <button className={`btn ${view === 'operation' ? 'btn-primary' : 'btn-secondary'}`}
          disabled={busy} aria-pressed={view === 'operation'}
          onClick={() => setView('operation')}>Operação</button>
        <button className={`btn ${view === 'history' ? 'btn-primary' : 'btn-secondary'}`}
          disabled={busy} aria-pressed={view === 'history'}
          onClick={() => setView('history')}>Histórico do posto</button>
      </nav>

      {notification && <p role="alert" className="break-words rounded border border-amber-300 bg-amber-50 p-3">{notification}</p>}
      {view === 'operation' && validWorkstation && session && <section aria-label="Próximo passo do registo"
        className="card space-y-2 border border-primary-200 p-4">
        <h2 className="font-semibold">{session.status === 'draft' ? 'Produção ainda não confirmada' : 'Produção confirmada'}</h2>
        {session.status === 'draft' ? <>
          <p className="text-sm">Guardar um consumo não conclui a produção. Reveja a quantidade produzida e confirme o registo para gerar as etiquetas dos contentores.</p>
          <button className="btn btn-primary" onClick={() => editorRef.current?.scrollIntoView({ block: 'start' })}>
            Rever produção e gerar etiquetas
          </button>
        </> : session.output_containers?.length ? <>
          <p className="text-sm">As etiquetas estão disponíveis. Descarregue o PDF de cada contentor e imprima a 100% em papel 60 × 40 mm.</p>
          <button className="btn btn-primary" onClick={() => outputsRef.current?.scrollIntoView({ block: 'start' })}>
            Ver etiquetas / imprimir
          </button>
        </> : <p className="text-sm">Este registo anterior não tem contentores de saída nem etiquetas. Não serão criados retroativamente.</p>}
      </section>}
      {uncertain && <button className="btn btn-primary" disabled={mutation.isPending || !serverAvailable}
        onClick={() => execute(uncertain)}>Repetir o mesmo pedido (mesmo identificador)</button>}
      {canManage && <CatalogImporter userId={userId} disabled={busy} pending={catalogMutation.isPending}
        uncertain={!!uncertainCatalog} retry={() => {
          if (!uncertainCatalog || inFlight.current) return
          inFlight.current = true
          catalogMutation.mutate(uncertainCatalog)
        }} importCatalog={catalog => {
          if (busy || inFlight.current) return
          inFlight.current = true
          setNotification('')
          catalogMutation.mutate(catalog)
        }} />}

      <section className="card space-y-4 p-4">
        <h2 className="font-semibold">{view === 'operation' ? 'Novo registo de consumo' : 'Posto de trabalho'}</h2>
        {view === 'operation' && <p className="text-sm text-gray-600">
          Selecione a máquina, escolha a ordem de produção e clique em “Iniciar registo”.
          Depois, leia o lote e adicione a quantidade consumida.
        </p>}
        <QueryState loading={workstations.isLoading} error={workstations.isError}
          empty={!workstations.data?.length} retry={() => void workstations.refetch()}>
          <label className="block text-sm">Posto de trabalho{' '}
            <select className="input mt-1 w-full" value={workstationId} disabled={busy}
              onChange={event => {
                setSelection({ workstationId: event.target.value, jobId: '', sessionId: '' })
                setNotification('')
              }}>
              <option value="">Selecione um posto</option>
              {workstations.data?.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select>
          </label>
          {!!workstationId && !validWorkstation && <p role="alert">O posto guardado já não está disponível. Selecione outro posto.</p>}
        </QueryState>
        {validWorkstation && view === 'operation' && (
          <QueryState loading={jobs.isLoading} error={jobs.isError} empty={!jobs.data?.length}
            errorMessage={jobs.isError ? errorText(jobs.error) : undefined}
            emptyContent={<div role="status" className="space-y-2 rounded border border-amber-300 bg-amber-50 p-3 text-amber-900">
              <p className="font-semibold">Sem ordens de produção disponíveis para iniciar um novo consumo neste posto.</p>
              <p className="text-sm">A lista só inclui OPs ativas, elegíveis e com dados ERP dentro do prazo de validade.
                Se esperava encontrar uma OP, peça ao responsável que verifique a atribuição à máquina e a sincronização com o ERP.</p>
              <p className="text-sm">Estar online não significa que os dados ERP estejam atualizados.
                Os registos anteriores continuam disponíveis em “Histórico do posto”.</p>
              <button className="btn btn-secondary" disabled={!serverAvailable || jobs.isFetching}
                onClick={() => void jobs.refetch()}>Atualizar lista de OPs</button>
              <p className="text-xs">Este botão volta a consultar o catálogo do Hub; não sincroniza dados com o ERP.</p>
            </div>}
            retry={() => void jobs.refetch()}>
            <label className="block text-sm">Trabalho / ordem de produção{' '}
              <select className="input mt-1 w-full" value={currentJobId} disabled={busy}
                onChange={event => {
                  setSelection(previous => ({ ...previous, jobId: event.target.value, sessionId: '' }))
                }}>
                <option value="">Selecione um trabalho</option>
                {jobs.data?.map(job => <option key={job.id} value={job.id}>
                  {job.production_order} · {job.order_line}/{job.component_line} · {job.material_item} · {job.description}
                </option>)}
              </select>
            </label>
            {selectedJob && <div className="grid gap-2 break-words text-sm sm:grid-cols-2">
              <p>Artigo produzido: {selectedJob.produced_item}{selectedJob.produced_description ? ` — ${selectedJob.produced_description}` : ''}</p>
              <p>Material: {selectedJob.material_item}{selectedJob.description ? ` — ${selectedJob.description}` : ''}</p>
              <p>Operação: {selectedJob.operation}</p><p>Localização: {selectedJob.location}</p>
              <p>Unidade de consumo: {selectedJob.consumption_uom}</p>
              <p>Unidade de produção: {selectedJob.production_uom}</p>
            </div>}
            {selectedJob && <QueryState loading={progress.isLoading} error={progress.isError}
              empty={!progress.data} retry={() => void progress.refetch()}>
              {progress.data && <JobQuantityReference job={selectedJob} progress={progress.data} />}
            </QueryState>}
            <button className="btn btn-primary" disabled={busy || !selectedJob || !!sessionId || !progress.data || progress.isError}
              onClick={() => execute({ kind: 'create', body: {
                id: crypto.randomUUID(), job_id: currentJobId, device_id: deviceId.current,
              } })}>Iniciar registo</button>
            {!!sessionId && <button className="btn btn-secondary ml-2" disabled={busy}
              onClick={() => {
                setSelection(previous => ({ ...previous, sessionId: '' }))
              }}>Preparar novo registo</button>}
          </QueryState>
        )}
      </section>

      {validWorkstation && view === 'history' && <section className="card space-y-3 p-4">
        <h2 className="font-semibold">Histórico do posto</h2>
        <p className="text-sm text-gray-600">Consulta independente da operação. Escolha um registo para consultar ou retomar um rascunho.</p>
        <QueryState loading={sessions.isLoading} error={sessions.isError} empty={!sessions.data?.length}
          retry={() => void sessions.refetch()}>
          <label className="block text-sm">Retomar ou consultar registo{' '}
            <select className="input mt-1 w-full" value={sessionId} disabled={busy}
              onChange={event => {
                const summary = sessions.data?.find(item => item.id === event.target.value)
                setSelection(previous => ({ ...previous, sessionId: summary?.id || '', jobId: summary?.job_id || previous.jobId }))
                setNotification('')
                setView('operation')
              }}>
              <option value="">Novo registo / selecione um registo existente</option>
              {sessions.data?.map(item => <option key={item.id} value={item.id}>
                {item.production_order} · {item.material_item} · {item.status === 'draft' ? 'Rascunho' : 'Confirmado'} · {item.id}
              </option>)}
            </select>
          </label>
        </QueryState>
      </section>}

      {view === 'operation' && validWorkstation && sessionId && <QueryState loading={detail.isLoading} error={detail.isError}
        empty={!session} retry={() => void detail.refetch()}>
        {session && <section className="card space-y-4 p-4">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0">
              <h2 className="font-semibold">{session.status === 'draft' ? 'Rascunho' : 'Produção confirmada'} · {session.production_order}</h2>
              <p className="break-all text-xs text-gray-500">Registo {session.id} · Revisão {session.revision} · Origem {session.origin_mode}</p>
            </div>
            <button className="btn btn-secondary" disabled={busy || detail.isFetching}
              onClick={() => void detail.refetch()}>Atualizar registo</button>
          </div>
          <dl className="grid gap-3 rounded bg-gray-50 p-3 sm:grid-cols-3">
            <div><dt className="text-sm text-gray-600">Consumo deste registo</dt><dd className="break-words font-semibold">{session.summary.consumed_quantity} {session.consumption_uom}</dd></div>
            <div><dt className="text-sm text-gray-600">Produção deste registo</dt><dd>{session.produced_quantity ?? 'Por confirmar'} {session.production_uom}</dd></div>
            <div><dt className="text-sm text-gray-600">Material / unidade produzida</dt><dd>{session.summary.material_per_output_unit ?? '—'}</dd></div>
          </dl>
          <div className="space-y-1 break-words text-sm">
            <p>Estado da integração (reportado pelo servidor): <strong>{integrationLabels[session.integration_status] ?? 'Não indicado'}</strong></p>
            <p>Referência ERP: {session.erp_reference ?? 'Não indicada'}</p>
            {session.correction_of && <p className="break-all">Registo original: {session.correction_of}</p>}
            {session.reason && <p>Motivo da correção: {session.reason}</p>}
          </div>
          <div ref={editorRef} className="space-y-4">
            <SessionEditor key={`editor:${session.id}`} session={session} progress={progress.data}
              disabled={busy || detail.isFetching} execute={execute} />
          </div>
          {session.status === 'confirmed' && <div ref={outputsRef}>
            <ProductionOutputs outputs={session.output_containers ?? []} disabled={busy || detail.isFetching} />
          </div>}
          {session.status === 'draft' && !session.correction_of && <LotEntry key={`lots:${session.id}:${session.revision}`} session={session}
            suggestedQuantity={progress.data?.remaining_expected ?? ''}
            lots={lots.data ?? []} loading={lots.isLoading} error={lots.isError}
            errorMessage={lots.isError ? errorText(lots.error) : undefined}
            refreshing={lots.isFetching} disabled={busy || detail.isFetching}
            refresh={() => void lots.refetch()} execute={execute} />}
        </section>}
      </QueryState>}
    </div>
  )
}

function JobQuantityReference({ job, progress }: Readonly<{ job: ProductionJob; progress: ProductionProgress }>) {
  return (
    <div className="space-y-2 rounded border border-gray-200 p-3 text-sm">
      <p className="font-medium">Acumulado da OP / linha / componente neste posto</p>
      <dl className="grid gap-2 break-words sm:grid-cols-2">
        <div><dt>Material já confirmado</dt><dd>{progress.confirmed_consumption} {job.consumption_uom}</dd></div>
        <div><dt>Rascunhos / ajuste em curso</dt><dd>{progress.draft_consumption} {job.consumption_uom}</dd></div>
        <div><dt>Material acumulado (inclui rascunhos)</dt><dd>{progress.total_consumption} {job.consumption_uom}</dd></div>
        {progress.remaining_expected != null && <div>
          <dt>Material por consumir (esperado − acumulado)</dt><dd className="font-semibold">{progress.remaining_expected} {job.consumption_uom}</dd>
        </div>}
        {progress.remaining_snapshot_estimate != null && <div>
          <dt>Saldo estimado ERP (snapshot − pendente Hub)</dt><dd>{progress.remaining_snapshot_estimate} {job.consumption_uom}</dd>
        </div>}
        <div><dt>Produção já confirmada</dt><dd>{progress.confirmed_output} {job.production_uom}</dd></div>
        {progress.remaining_output != null && <div><dt>Produção por realizar</dt><dd>{progress.remaining_output} {job.production_uom}</dd></div>}
      </dl>
      <details>
        <summary className="cursor-pointer">Consultar referência ERP original (snapshot)</summary>
        <dl className="mt-2 grid gap-2 break-words sm:grid-cols-2">
        {job.expected_quantity != null && <div>
          <dt>Material esperado (referência)</dt><dd>{job.expected_quantity} {job.consumption_uom}</dd>
        </div>}
        {job.remaining_quantity != null && <div>
          <dt>Material restante (snapshot)</dt><dd>{job.remaining_quantity} {job.consumption_uom}</dd>
        </div>}
        {job.planned_output != null && <div>
          <dt>Produção planeada (referência)</dt><dd>{job.planned_output} {job.production_uom}</dd>
        </div>}
        {job.qty_per_unit_of_measure != null && <div>
          <dt>Fator bruto ERP de unidade de medida</dt><dd>{job.qty_per_unit_of_measure}</dd>
        </div>}
        </dl>
      </details>
      <p className="text-xs text-gray-600">
        {progress.open_drafts} rascunho(s) aberto(s). Confirmações são definitivas.
        Saldos negativos indicam excesso. O saldo ERP é uma estimativa, dependente da atualização do snapshot e dos reconhecimentos de integração.
        O fator bruto ERP não representa consumo por unidade produzida. Não altera quantidades registadas nem reserva stock.
      </p>
    </div>
  )
}

const integrationLabels: Record<ProductionSession['integration_status'], string> = {
  not_acknowledged: 'Sem reconhecimento ERP',
  imported: 'Importado',
  posted: 'Contabilizado / posted',
  rejected: 'Rejeitado',
}

function CatalogImporter({ userId, disabled, pending, uncertain, retry, importCatalog }: Readonly<{
  userId: number
  disabled: boolean
  pending: boolean
  uncertain: boolean
  retry: () => void
  importCatalog: (catalog: ProductionCatalog) => void
}>) {
  const [json, setJson] = useState('')
  const [acknowledged, setAcknowledged] = useState(false)
  const [error, setError] = useState('')
  const [sample] = useState(() => JSON.stringify(catalogExample(userId), null, 2))
  return (
    <details className="card p-4">
      <summary className="cursor-pointer font-semibold">Administração · Importar catálogo de produção (JSON)</summary>
      <form className="mt-4 space-y-3" onSubmit={event => {
        event.preventDefault()
        if (disabled || !acknowledged) return
        let value: unknown
        try {
          value = JSON.parse(json)
        } catch {
          setError('JSON inválido. Verifique a sintaxe antes de importar.')
          return
        }
        const result = productionCatalogSchema.safeParse(value)
        if (!result.success) {
          setError(result.error.issues.map(issue => `${issue.path.join('.') || 'catálogo'}: ${issue.message}`).join('; '))
          return
        }
        setError('')
        if (window.confirm('Importar este catálogo? Os acessos dos postos incluídos serão substituídos pelos acessos enviados. A ausência de acessos pode remover as permissões existentes.')) {
          importCatalog(result.data)
        }
      }}>
        <p className="rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          Atenção: os acessos (grants) dos postos incluídos em workstations serão substituídos pela lista access enviada.
          Inclua todos os utilizadores que devem manter acesso a esses postos. Uma lista sem acessos para um posto remove os acessos existentes.
        </p>
        <p className="text-sm text-gray-600">
          O tenant é determinado pela autenticação, nunca pelo JSON. Use UUIDs estáveis para atualizações;
          user_id é um identificador numérico. updated_at usa ISO UTC. O exemplo gera novos UUIDs e deve ser adaptado antes da importação.
        </p>
        <button type="button" className="btn btn-secondary" disabled={disabled}
          onClick={() => { setJson(sample); setAcknowledged(false); setError('') }}>Preencher exemplo (não importa)</button>
        <div>
          <label htmlFor="production-catalog-json" className="block text-sm">Catálogo JSON</label>
          <textarea id="production-catalog-json" className="input mt-1 min-h-72 w-full font-mono text-xs" value={json}
            placeholder={sample} spellCheck={false} disabled={disabled}
            onChange={event => { setJson(event.target.value); setAcknowledged(false); setError('') }} />
        </div>
        <label className="flex items-start gap-2 text-sm">
          <input type="checkbox" className="mt-1" checked={acknowledged} disabled={disabled}
            onChange={event => setAcknowledged(event.target.checked)} />{' '}
          Compreendo que os acessos dos postos incluídos serão substituídos.
        </label>
        {error && <p role="alert" className="break-words text-sm text-red-700">{error}</p>}
        <button type="submit" className="btn btn-primary" disabled={disabled || !acknowledged || !json.trim()}>
          {pending ? 'A importar...' : 'Importar catálogo'}
        </button>
        {uncertain && <button type="button" className="btn btn-secondary ml-2" disabled={pending}
          onClick={retry}>Repetir o mesmo catálogo</button>}
      </form>
    </details>
  )
}

function SessionEditor({ session, progress, disabled, execute }: Readonly<{
  session: ProductionSession
  progress?: ProductionProgress
  disabled: boolean
  execute: (command: ProductionCommand) => void
}>) {
  const [produced, setProduced] = useState('')
  const [outputEdited, setOutputEdited] = useState(false)
  const [checking, setChecking] = useState(false)
  const [containerQuantities, setContainerQuantities] = useState('')
  const quantities = containerQuantities.trim() ? containerQuantities.split(';').map(value => value.trim()) : undefined
  const draft = session.status === 'draft' && !session.correction_of
  const validOutput = session.lines.length > 0 && positiveDecimal(produced.trim()) &&
    (!quantities || (quantities.length <= 100 && quantities.every(positiveDecimal)))
  useEffect(() => {
    if (!outputEdited && progress?.remaining_output && positiveDecimal(progress.remaining_output)) {
      setProduced(progress.remaining_output)
    }
  }, [outputEdited, progress?.remaining_output])

  async function confirmProduction(event: FormEvent) {
    event.preventDefault()
    if (!validOutput || disabled || checking) return
    setChecking(true)
    try {
      const check = await productionApi.quantityCheck(session.id, produced.trim())
      const warning = check.deviation
        ? `Consumo diferente do previsto: para ${produced} ${session.production_uom}, a OP prevê ${check.expected_consumption} ${check.consumption_uom}; registado ${check.actual_consumption} ${check.consumption_uom}.\n\n`
        : ''
      if (window.confirm(`${warning}Confirmar produção de ${produced} ${session.production_uom} em ${quantities?.length ?? 1} contentor(es)? Após confirmar não poderá editar, corrigir ou anular.`)) {
        execute({ kind: 'confirm', sessionId: session.id,
          body: { expected_revision: session.revision, produced_quantity: produced.trim(),
            ...(quantities ? { container_quantities: quantities } : {}) } })
      }
    } catch (error) {
      toast.error(errorText(error))
    } finally {
      setChecking(false)
    }
  }
  return (
    <>
      {draft && <label className="block rounded border p-3 text-sm">Quantidade produzida / a produzir ({session.production_uom})
        <input className="input mt-1 w-full sm:w-56" inputMode="decimal" value={produced}
          onChange={event => { setOutputEdited(true); setProduced(event.target.value) }} required disabled={disabled || checking} />
        <span className="mt-1 block text-xs text-gray-600">Sugestão: produção por realizar da OP. Pode alterar; confirma no final.</span>
      </label>}
      <h3 className="font-semibold">Consumos registados</h3>
      {!session.lines.length && <p className="text-gray-500">Ainda não existem consumos.</p>}
      <div className="space-y-3">
        {session.lines.map(line => (
          <form key={`${line.id}:${session.revision}`} className="flex flex-wrap items-end gap-3 rounded border p-3"
            onSubmit={event => {
              event.preventDefault()
              const value = new FormData(event.currentTarget).get('quantity')
              const quantity = typeof value === 'string' ? value.trim() : ''
              if (!positiveDecimal(quantity)) return toast.error('Introduza uma quantidade decimal positiva (ponto decimal).')
              execute({ kind: 'update', sessionId: session.id, lineId: line.id,
                body: { expected_revision: session.revision, quantity } })
            }}>
            <div className="min-w-0 flex-1 basis-40 break-words">
              <p className="font-medium">Lote {line.lot}</p>
              <p className="text-sm">Contentor {line.container_reference} · {line.container_source === 'scanned' ? 'Lido' : 'Declarado'}</p>
            </div>
            <label className="min-w-0 text-sm">Quantidade ({session.consumption_uom})
              <input aria-label={`Quantidade do lote ${line.lot}, contentor ${line.container_reference}`}
                className="input mt-1 w-full sm:w-36" name="quantity" inputMode="decimal" required
                defaultValue={line.quantity} disabled={disabled || !draft} />
            </label>
            {draft && <>
              <button className="btn btn-secondary" disabled={disabled} type="submit">Guardar</button>
              <button className="btn btn-secondary text-red-700" disabled={disabled} type="button"
                onClick={() => {
                  if (window.confirm(`Remover consumo do lote ${line.lot}, contentor ${line.container_reference}?`)) {
                    execute({ kind: 'remove', sessionId: session.id, lineId: line.id,
                      body: { expected_revision: session.revision } })
                  }
                }}>Remover</button>
            </>}
          </form>
        ))}
      </div>
      {draft ? <form className="space-y-3 rounded border border-primary-200 p-3" onSubmit={event => void confirmProduction(event)}>
        <h3 className="font-semibold">Concluir produção</h3>
        <p className="text-sm text-amber-900">Reveja os dados: após confirmar não poderá editar, corrigir ou anular. Eventuais enganos terão de ser tratados pelo responsável ERP.</p>
        <p>Quantidade a confirmar: {produced || 'Indique a quantidade acima'} {session.production_uom}</p>
        <label className="block text-sm">Quantidades por contentor (opcional, separadas por ponto e vírgula)
          <input className="input mt-1 w-full" value={containerQuantities} placeholder="Ex.: 12; 8"
            onChange={event => setContainerQuantities(event.target.value)} disabled={disabled} />
        </label>
        <p className="text-xs text-gray-600">Vazio = um contentor com o total. A soma deve ser igual à quantidade produzida; a API valida antes de confirmar. Até 100 contentores.</p>
        <button type="submit" className="btn btn-primary" disabled={disabled || checking || !validOutput}>
          Confirmar produção
        </button>
      </form> : <div className="space-y-3">
        <p className="text-sm text-gray-600">Registo apenas para consulta. Não é permitido editar, corrigir ou anular após confirmação. Contacte o responsável ERP para tratar eventuais enganos.</p>
      </div>}
    </>
  )
}

function LotEntry({ session, suggestedQuantity, lots, loading, error, errorMessage, refreshing, disabled, refresh, execute }: Readonly<{
  session: ProductionSession
  suggestedQuantity: string
  lots: ProductionLot[]
  loading: boolean
  error: boolean
  errorMessage?: string
  refreshing: boolean
  disabled: boolean
  refresh: () => void
  execute: (command: ProductionCommand) => void
}>) {
  const [scan, setScan] = useState('')
  const [lot, setLot] = useState('')
  const [container, setContainer] = useState('')
  const [source, setSource] = useState<'scanned' | 'declared'>('declared')
  const [quantity, setQuantity] = useState(positiveDecimal(suggestedQuantity) ? suggestedQuantity : '')
  const [quantityEdited, setQuantityEdited] = useState(false)
  const [showLots, setShowLots] = useState(false)
  const [camera, setCamera] = useState<'lot' | 'container'>()
  const [allowReuse, setAllowReuse] = useState(false)
  const [scanError, setScanError] = useState('')
  const [resolved, setResolved] = useState<ProductionGenealogy>()
  const [resolving, setResolving] = useState(false)
  const scanInput = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (!quantityEdited && positiveDecimal(suggestedQuantity)) setQuantity(suggestedQuantity)
  }, [quantityEdited, suggestedQuantity])

  function choose(candidate: ProductionLot) {
    setResolved(undefined)
    setLot(candidate.lot)
    setContainer(candidate.container_reference ?? '')
    setSource('declared')
    setQuantity(positiveDecimal(suggestedQuantity) ? suggestedQuantity : candidate.container_quantity ?? '')
    setQuantityEdited(false)
    setAllowReuse(false)
    setScanError('')
  }

  async function validateCode(code: string) {
    if (hubContainerCode(code)) {
      setResolving(true)
      setScanError('')
      setResolved(undefined)
      setLot('')
      try {
        const data = await productionApi.resolveContainer(session.job_id, code.trim())
        setResolved(data)
        setLot(data.lot)
        setContainer(data.qr_payload)
        setSource('scanned')
        setQuantity(data.available_quantity)
        setQuantityEdited(true)
        setAllowReuse(false)
        setScan('')
      } catch (error) {
        setScanError(errorText(error))
      } finally {
        setResolving(false)
      }
      return
    }
    // A wedge submits an exact lot identifier, not a parsed or fabricated lot.
    setResolving(true)
    setScanError('')
    setResolved(undefined)
    setLot('')
    try {
      const candidates = await productionApi.resolveLot(session.job_id, code.trim())
      if (candidates.length === 0) {
        setScanError('Lote sem stock disponível. Leia o identificador exato ou atualize os lotes.')
        return
      }
      if (candidates.length > 1) {
        setScanError('O lote tem vários contentores. Selecione uma linha em “Lotes disponíveis”.')
        setShowLots(true)
        return
      }
      choose(candidates[0])
      setScan('')
      scanInput.current?.focus()
    } catch (error) {
      setScanError(errorText(error))
    } finally {
      setResolving(false)
    }
  }

  async function addLine(event: FormEvent) {
    event.preventDefault()
    if (disabled || resolving) return
    if (!lot) return toast.error('Valide um lote antes de adicionar o consumo.')
    if (!positiveDecimal(quantity.trim())) return toast.error('Introduza uma quantidade positiva (ponto decimal).')
    const duplicate = session.lines.some(line => line.container_reference === container.trim())
    let reuse = allowReuse
    if (duplicate && !reuse) {
      reuse = window.confirm('Este contentor já foi utilizado neste registo. Autoriza explicitamente a reutilização?')
      if (!reuse) return
    }
    setResolving(true)
    try {
      if (!resolved) await productionApi.resolveLot(session.job_id, lot)
      execute({ kind: 'add', sessionId: session.id, body: {
        expected_revision: session.revision, scan_id: crypto.randomUUID(), lot,
        container_reference: container.trim(), container_source: source,
        quantity: quantity.trim(), allow_reuse: reuse,
      } })
    } catch (error) {
      setScanError(errorText(error))
    } finally {
      setResolving(false)
    }
  }

  return (
    <section className="space-y-3 border-t pt-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-semibold">Adicionar consumo</h3>
        <button type="button" className="btn btn-secondary" disabled={disabled}
          onClick={() => setShowLots(true)}>Lotes disponíveis</button>
      </div>
        <fieldset disabled={disabled || refreshing || resolving} className="min-w-0 space-y-3">
          <form className="flex flex-wrap items-end gap-2" onSubmit={event => { event.preventDefault(); void validateCode(scan) }}>
            <label className="min-w-0 flex-1 text-sm">Ler lote ou QR de contentor Hub (leitor USB/Bluetooth + Enter){' '}
              <span className="relative mt-1 block">
                <input ref={scanInput} className="input w-full pr-12" value={scan}
                  autoComplete="off" onChange={event => { setScan(event.target.value); setLot(''); setResolved(undefined) }} />
                <button type="button" className="absolute inset-y-0 right-0 px-3" aria-label="Ler lote com câmara"
                  onClick={() => setCamera('lot')}><ScanLine size={20} /></button>
              </span>
            </label>
            <button type="submit" className="btn btn-secondary" disabled={!scan}>Validar código lido</button>
          </form>
          {scanError && <p role="alert" className="text-red-700">{scanError}</p>}
          {resolved && <details><summary className="cursor-pointer text-sm">Consultar origens do contentor</summary><Genealogy data={resolved} /></details>}
          {error && <p role="alert" className="text-sm text-amber-800">Lista de lotes indisponível: {errorMessage} A leitura exata continua sujeita à validação do servidor.</p>}
          {showLots && <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/60 p-4"
            role="dialog" aria-modal="true" aria-label="Lotes disponíveis">
            <div className="w-full max-w-3xl space-y-3 rounded bg-white p-4">
              <div className="flex flex-wrap justify-between gap-2">
                <h3 className="font-semibold">Lotes disponíveis</h3>
                <button type="button" className="btn btn-secondary" onClick={refresh}>Atualizar lotes</button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowLots(false)}>Fechar lotes</button>
              </div>
              <p className="text-sm">Disponibilidade no snapshot, sujeita à validação no servidor.</p>
          <QueryState loading={loading} error={error} errorMessage={errorMessage} empty={!lots.length} retry={refresh}>
          <ul className="grid max-h-80 gap-2 overflow-y-auto sm:grid-cols-2">
            {lots.map((candidate, index) => (
              <li key={`${candidate.lot}:${candidate.container_reference ?? ''}:${index}`}>
                <button type="button" className={`w-full break-words rounded border p-3 text-left text-sm ${lot === candidate.lot && container === (candidate.container_reference ?? '') ? 'border-primary-500 bg-primary-50' : 'hover:bg-gray-50'}`}
                  onClick={() => {
                    if (candidate.container_reference?.startsWith('SFH1:C:')) {
                      void validateCode(candidate.container_reference)
                    } else {
                      choose(candidate)
                    }
                    setShowLots(false)
                  }}>
                  <strong>Lote {candidate.lot}</strong>
                  <p>Disponível no lote: {candidate.quantity_available} {session.consumption_uom}</p>
                  <p>Contentor: {candidate.container_reference ?? 'Não identificado'} · Quantidade do contentor: {candidate.container_quantity ?? 'Não indicada'}</p>
                  <p className="text-xs text-gray-500">Snapshot: {candidate.updated_at}</p>
                </button>
              </li>
            ))}
          </ul>
          </QueryState>
            </div>
          </div>}
          <form className="space-y-3 rounded border p-3" onSubmit={event => void addLine(event)}>
            <p className="font-medium">Lote selecionado: {lot || 'Nenhum'}</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="text-sm">Identificador do contentor (evidência obrigatória){' '}
                <span className="relative mt-1 block">
                  <input className="input w-full pr-12" required value={container}
                    onChange={event => { setContainer(event.target.value); setSource('declared'); setAllowReuse(false); setResolved(undefined) }}
                    onKeyDown={event => {
                      if (event.key === 'Enter' && hubContainerCode(container)) {
                        event.preventDefault()
                        void validateCode(container)
                      }
                    }} />
                  <button type="button" className="absolute inset-y-0 right-0 px-3" aria-label="Ler contentor com câmara"
                    onClick={() => setCamera('container')}><ScanLine size={20} /></button>
                </span>
              </label>
              <label className="text-sm">Origem da identificação{' '}
                <select className="input mt-1 w-full" value={source}
                  onChange={event => setSource(event.target.value === 'scanned' ? 'scanned' : 'declared')}>
                  <option value="declared">Declarado manualmente</option>
                  <option value="scanned">Lido com scanner no campo de identificação</option>
                </select>
              </label>
              <label className="text-sm">Quantidade consumida ({session.consumption_uom})
                <input className="input mt-1 w-full" inputMode="decimal" required value={quantity}
                  onChange={event => { setQuantityEdited(true); setQuantity(event.target.value) }} />
              </label>
            </div>
            <p className="text-xs text-gray-600">Sugestão: material por consumir da linha da OP; se não existir, quantidade específica do contentor. Confirme a quantidade real.</p>
            <label className="flex items-start gap-2 text-sm">
              <input type="checkbox" className="mt-1" checked={allowReuse} onChange={event => setAllowReuse(event.target.checked)} />{' '}
              Autorizo explicitamente reutilizar este contentor (incluindo utilizações noutros registos).
            </label>
            <button type="submit" className="btn btn-primary" disabled={!lot || !container.trim() || !positiveDecimal(quantity.trim())}>Adicionar consumo</button>
          </form>
        </fieldset>
        {camera && <ProductionScanner onClose={() => setCamera(undefined)} onRead={code => {
          const field = camera
          setCamera(undefined)
          if (field === 'lot' || hubContainerCode(code)) {
            void validateCode(code)
          } else {
            setContainer(code.trim())
            setResolved(undefined)
            setSource(lots.some(candidate => candidate.lot === lot && candidate.container_reference === code.trim()) ? 'scanned' : 'declared')
          }
        }} />}
    </section>
  )
}
